<template>
  <!-- Each slide fills the frame, the image covers, and the overlay sits on the left 50% -->
  <div class="cis-carousel-slide">
    <div class="cis-carousel-slide__frame">
      <div class="cis-carousel-slide__media">
        <!-- sc-image outputs an <img>; style the inner img to cover -->
        <sc-image
          v-if="slide?.fields?.image"
          :media="slide.fields.image"
          :attrs="imageAttrs"
          class="cis-carousel-slide__image"
        />
      </div>

      <!-- Full overlay layer pinned to the slide frame -->
      <div class="cis-carousel-slide__overlay">
        <!-- The dark transparent panel positioned by slide-relative percentages -->
        <div class="cis-carousel-slide__panel">
          <!-- Inner inset wrapper to create 10% vertical + 5% horizontal padding of the PANEL -->
          <div class="cis-carousel-slide__panel-inner">
            <!-- ISSUE - h1 and h2 color cannot be overidden from blue-->
            <sc-text
              v-if="slide?.fields?.h1Text"
              :field="slide.fields.h1Text"
              tag="h4"
              class="cis-carousel-slide__h2"
            />
            <sc-text
              v-if="slide?.fields?.h3Text"
              :field="slide.fields.h3Text"
              tag="h4"
              class="cis-carousel-slide__h4"
            />
            <sc-link v-if="hasLink" :field="slide.fields.ctaLink" class="cis-carousel-slide__cta">
              <sc-text v-if="slide?.fields?.ctaText" :field="slide.fields.ctaText" tag="span" />
            </sc-link>
            <!-- Fallback if ScLink slot isn't supported -->
            <a
              v-else-if="slide?.fields?.ctaLink?.value?.href"
              :href="slide.fields.ctaLink.value.href"
              class="cis-carousel-slide__cta"
            >
              {{
                slide?.fields?.ctaText?.value || slide?.fields?.ctaLink?.value?.text || 'Learn more'
              }}
            </a>
          </div>
        </div>
      </div>
      <!-- /overlay -->
    </div>
  </div>
</template>

<script>
import { Image, Text, Link } from '@sitecore-jss/sitecore-jss-vue';

export default {
  name: 'CIS-Carousel-Slide',
  components: {
    ScImage: Image,
    ScText: Text,
    ScLink: Link
  },
  props: {
    slide: {
      type: Object,
      required: true
    },
    slideIndex: {
      type: Number,
      required: true
    },
    inEditor: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    hasLink() {
      return !!this.slide?.fields?.ctaLink?.value?.href;
    },
    imageAttrs() {
      // First visible slide = eager/high priority
      if (this.slideIndex === 0) {
        return {
          loading: 'eager',
          fetchpriority: 'high'
        };
      }

      // All others lazy
      return {
        loading: 'lazy',
        fetchpriority: 'auto'
      };
    }
  }
};
</script>

<style lang="scss" scoped>
.cis-carousel-slide {
  width: 100%;
  background: transparent; /* fallback if image missing */

  &__frame {
    position: relative;
    width: 100%;
    margin: 0 auto;
    border-width: 2px 0px 2px 0px;
    border-style: solid none solid none;
    border-color: #ddd;
    box-shadow:
      -12px 0px 24px rgba(0, 0, 0, 0.15),
      /* left */ 12px 0px 24px rgba(0, 0, 0, 0.15),
      /* right */ 0px 24px 32px rgba(0, 0, 0, 0.225); /* bottom */
  }

  /* Image gives the frame its natural height */
  &__media {
    position: relative;
    overflow: hidden;
    pointer-events: none;
  }

  &__image {
    width: 100%;
    height: auto;
    display: block;
    pointer-events: none;
  }

  &__image :deep(img) {
    width: 100%;
    height: auto;
    display: block;
    object-fit: cover;
  }

  /* Full overlay layer that spans the slide frame */
  &__overlay {
    position: absolute;
    inset: 0;
    pointer-events: auto; /* allow carousel controls through */
  }

  /* === Transparent midnight-blue panel positioned by slide-relative percentages === */
  &__panel {
    position: absolute;
    left: 5%;
    right: 50%;
    top: 10%;
    bottom: 15%;
    background: rgba(0, 59, 92, 0.875);
    border-radius: 10px;
    outline: 1px solid rgba(255, 255, 255, 0.08);
  }

  &__panel-inner {
    inset: 10% 7.5%; /* top/right/bottom/left: 10% 5% 10% 5% */
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: clamp(1rem, 1.5vw, 3rem);
    color: #fff;
    text-align: left;
    position: absolute;
    z-index: 4;
    pointer-events: auto; /* text and CTA are clickable */
  }

  &__h2 {
    margin: 0 0 0 0;
    @include font-styles('Arial Narrow', 4.6rem, 400, normal, normal, 0, #fff);
    font-size: clamp(1.8rem, 2.8vw, 4.6rem);
    line-height: 1.1;
    font-weight: 400;
    color: #fff !important;
  }

  &__h4 {
    margin: 0 0 0 0;
    font-size: clamp(1.2rem, 1.8vw, 2.8rem);
    line-height: 1.3;
    font-weight: 300;
    opacity: 0.95;
    color: #fff;
  }

  &__cta {
    display: inline;
    padding: clamp(0.3rem, 1vw, 0.9rem) /* Vertical (min, preferred, max) */
      clamp(0.5rem, 1.8vw, 1.6rem); /* Horizontal (min, preferred, max) */
    @include font-styles('Arial', 1.8rem, 700, normal, normal, 0, rgba(0, 59, 92, 1));
    color: rgba(0, 59, 92, 1);
    font-size: clamp(1rem, 1.5vw, 1.8rem);
    background-color: #fa9917;
    text-decoration: none;
    border-radius: 5px;
    /* shadow for contrast on light images */
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
    margin-top: auto;
    align-self: flex-start;
    position: relative;
    z-index: 3;
  }

  &__cta:hover {
    background-color: #d56c00;
    color: rgb(1, 45, 71);
  }

  &__cta span {
    pointer-events: none;
  }

  /* Experience Editor stack */
  :host(.is-editing) &__frame,
  .is-editing &__frame {
    height: auto;
    min-height: 420px;
    position: relative;
  }

  .is-editing &__media {
    position: relative;
    height: auto;
  }

  .is-editing &__image :deep(img) {
    height: auto;
    max-height: 520px;
    object-fit: contain;
  }

  .is-editing &__overlay {
    position: static;
    width: 100%;
    height: auto;
    padding: 1rem 0 0 0;
  }

  .is-editing &__panel,
  .is-editing &__panel-inner {
    position: static;
    inset: auto;
    background: rgba(0, 24, 72, 0.15); /* lighter in editor for visibility */
    padding: 1rem;
    pointer-events: auto;
  }
}

@media (max-width: 1400px) {
  /* On mobile, allow panel to span most of the width for readability */
  .cis-carousel-slide__panel {
    right: 42%;
  }
}

@media (max-width: 1200px) {
  .cis-carousel-slide__panel {
    left: 6%;
    right: 25%;
    top: 8%;
    bottom: 8%;
  }
}

@media (max-width: 768px) {
  .cis-carousel-slide__panel {
    right: 6%;
    top: 6%;
    bottom: 6%;
  }
}

@media (max-width: 440px) {
  .cis-carousel-slide__h4 {
    display: none;
  }
}
</style>
